import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Container,
  Grid,
  Card,
  CardActionArea,
  Chip,
  CircularProgress,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { getAllProducts } from "../../../../../services/Public/PublicPagesApi";
import { getImageUrl } from "../../../../../services/indexMediaBaseUrl";

const NewArrivalsSection = () => {
  const { t, i18n } = useTranslation();
  let lang = (i18n && i18n.language) || localStorage.getItem("lang") || "ar";
  if (lang === "rn") lang = "en";
  lang = lang.startsWith("ar") ? "ar" : "en";

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const MAX_ITEMS = 8;

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        const res = await getAllProducts();
        if (res && res.success && Array.isArray(res.products)) {
          // flatten products arrays from each category entry
          const flat = [];
          res.products.forEach((entry) => {
            if (Array.isArray(entry.products)) {
              flat.push(...entry.products);
            }
          });
          // sort by created_at desc (newest first)
          flat.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
          setProducts(flat.slice(0, MAX_ITEMS));
        } else {
          setProducts([]);
        }
      } catch (err) {
        setError(err.message || "Error");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const getName = (p) => (lang === "ar" ? p.name_ar || p.name_en : p.name_en || p.name_ar);
  const getImage = (p) => getImageUrl(p.main_photo || (p.images && p.images[0] && p.images[0].image) || "");

  // helpers: pick price entry by stored country_id
  const getSelectedCountryId = () => {
    try {
      const v = localStorage.getItem("country_id");
      return v !== null ? String(v) : null;
    } catch {
      return null;
    }
  };

  const getPriceInfo = (product) => {
    if (!product) return null;
    const countryId = getSelectedCountryId();
    const curr = product.currencies || product.Currencies || [];
    let entry = null;
    if (countryId) entry = curr.find((c) => String(c.country_id) === String(countryId));
    if (!entry && curr.length) entry = curr[0];
    if (!entry) {
      const fallback = product.price || product.price_after_discount || null;
      return fallback ? { price: String(product.price || ""), price_after_discount: product.price_after_discount ? String(product.price_after_discount) : null } : null;
    }
    const rawPrice = entry.price != null ? String(entry.price) : null;
    const rawDiscount =
      entry.price_after_discount != null
        ? String(entry.price_after_discount)
        : null;

    const priceNum = parseFloat(rawPrice || "");
    const discountNum = parseFloat(rawDiscount || "");
    const hasValidDiscount =
      Number.isFinite(discountNum) &&
      discountNum > 0 &&
      (!Number.isFinite(priceNum) || discountNum < priceNum);

    return {
      price: rawPrice,
      price_after_discount: hasValidDiscount ? rawDiscount : null,
    };
  };

  const formatPrice = (p) => (p == null ? "" : p);

  if (loading) {
    return (
      <Box sx={{ py: 5, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 5, textAlign: "center" }}>
        <Typography color="error">
          {t("error_loading_new_arrivals", { defaultValue: lang === "ar" ? "حدث خطأ في تحميل الوصل حديثاً" : "Error loading new arrivals" })}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 5, px: 2 }}>
      <Container maxWidth="lg">
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
          <Typography variant="h4" fontWeight="bold" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AutoAwesomeIcon color="primary" /> {t("new_arrivals_title", { defaultValue: lang === "ar" ? "وصل حديثاً" : "New arrivals" })}
          </Typography>
          <Typography component={Link} to="/new-arrivals" sx={{ color: "primary.main", textDecoration: "none", fontWeight: "bold" }}>
            {t("view_all", { defaultValue: lang === "ar" ? "عرض الكل" : "View all" })}
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {products.map((product) => (
            <Grid item xs={12} sm={6} md={3} key={product.id}>
              <Card sx={{ height: "100%", position: "relative", "&:hover": { boxShadow: 4 } }}>
                <Chip label={t("new_badge", { defaultValue: lang === "ar" ? "جديد" : "New" })} color="success" size="small" sx={{ position: "absolute", top: 10, right: 10, zIndex: 1 }} />
                <CardActionArea component={Link} to={`/product/${product.id}`} sx={{ p: 3, textAlign: "center" }}>
                  <Box component="img" src={getImage(product)} alt={getName(product)} sx={{ width: 80, height: 80, objectFit: "cover", mb: 2, borderRadius: 1 }} />
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    {getName(product)}
                  </Typography>
                  <Typography variant="h6" color="primary" fontWeight="bold">
                    {(() => {
                      const pinfo = getPriceInfo(product);
                      if (!pinfo) return "";
                      const orig = formatPrice(pinfo.price);
                      const disc = formatPrice(pinfo.price_after_discount);
                      return disc ? (
                        <span>
                          <span style={{ textDecoration: "line-through", color: "#777", display: "block" }}>{orig} ج.م</span>
                          <span style={{ color: "inherit", fontWeight: 700 }}>{disc} ج.م</span>
                        </span>
                      ) : `${orig} ج.م`;
                    })()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mt={1}>
                    {lang === "ar" ? (product.desc_ar || "") : (product.desc_en || "")}
                  </Typography>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default NewArrivalsSection;
